import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  codeDispatchSchema,
  profileEnvelopeSchema,
  updatedProfileSchema,
  type CodeDispatch,
  type ProfilePanel,
  type UpdateProfileInput,
} from './schemas';

/**
 * The Profile panel body. A failed call throws — it is never cached as a result, so the next open
 * refetches it; the panel renders a failure as "everything unavailable" (spec: Profile panel,
 * States). A 401 reaches the refresh / force-logout seam the same way.
 */
export async function fetchProfile(): Promise<ProfilePanel> {
  return profileEnvelopeSchema.parse(await apiClient.getEnvelope(endpoints.profile.me));
}

export async function sendChangePasswordOtp(): Promise<CodeDispatch> {
  return codeDispatchSchema.parse(await apiClient.post(endpoints.profile.changePassword.sendOtp));
}

/** Rotates the `referenceId`; the server re-sends the same code with a fresh validity window. */
export async function resendChangePasswordOtp(referenceId: string): Promise<CodeDispatch> {
  return codeDispatchSchema.parse(
    await apiClient.post(endpoints.profile.changePassword.resendOtp, { referenceId }),
  );
}

export function verifyChangePasswordOtp(body: { referenceId: string; otp: string }) {
  return apiClient.post(endpoints.profile.changePassword.verifyOtp, body);
}

/** `{ newPassword }` only — the BE takes no confirmPassword. */
export function changePassword(newPassword: string) {
  return apiClient.put(endpoints.profile.changePassword.update, { newPassword });
}

/** `PUT /profile/tracking` — the reply is the Grower's saved identity. */
export async function updateProfile(input: UpdateProfileInput) {
  return updatedProfileSchema.parse(await apiClient.put(endpoints.profile.me, input));
}

export type PhoneStage = 'current' | 'new';
const changePhone = endpoints.profile.changePhone;

export async function sendCurrentPhoneOtp(): Promise<CodeDispatch> {
  return codeDispatchSchema.parse(await apiClient.post(changePhone.current.sendOtp));
}

/** `{ phone }` as 10 digits; the BE normalises and re-validates it. */
export async function sendNewPhoneOtp(phone: string): Promise<CodeDispatch> {
  return codeDispatchSchema.parse(await apiClient.post(changePhone.new.sendOtp, { phone }));
}

export async function resendPhoneOtp(
  stage: PhoneStage,
  referenceId: string,
): Promise<CodeDispatch> {
  return codeDispatchSchema.parse(
    await apiClient.post(changePhone[stage].resendOtp, { referenceId }),
  );
}

/** Verify-new also commits the change and revokes every session (`{ phone, sessionsRevoked }`). */
export function verifyPhoneOtp(stage: PhoneStage, body: { referenceId: string; otp: string }) {
  return apiClient.post(changePhone[stage].verifyOtp, body);
}
